import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@fastnorth/kraken/components/button';
import { Div } from '@fastnorth/kraken/components/layout';
import { ArrowUp } from 'lucide-react';
import { backToTopButtonVariants, backToTopRootVariants } from './BackToTop.styles';

export type BackToTopProps = {
  scrollToElement: HTMLDivElement | null;
};

const BackToTop = ({ scrollToElement }: BackToTopProps): ReactElement => {
  const { t } = useTranslation('translation', { keyPrefix: 'karwa.event_progress_v2' });

  const handleClick = (): void => {
    if (!scrollToElement) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    scrollToElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const focusTarget = (): void => {
      scrollToElement.focus({ preventScroll: true });
    };

    if ('onscrollend' in window) {
      const handleScrollEnd = (): void => {
        window.removeEventListener('scrollend', handleScrollEnd);
        focusTarget();
      };

      window.addEventListener('scrollend', handleScrollEnd, { once: true });
      return;
    }

    window.setTimeout(focusTarget, 400);
  };

  return (
    <Div className={backToTopRootVariants()} data-testid="event-progress-v2-back-to-top">
      <Button
        aria-label={t('back_to_top_button')}
        className={backToTopButtonVariants()}
        onClick={handleClick}
        rounded="default"
        size="icon"
        type="button"
        variant="default">
        <ArrowUp aria-hidden size={20} strokeWidth={2} />
      </Button>
    </Div>
  );
};

export default BackToTop;
